import { useState, useCallback, useMemo } from 'react';
import { Stairwell } from '../../types/stairwells';
import { stairwellsApi } from '../../api/stairwellsApi';
import { sortedStairwells as sortStairwells } from '../../Helpers/stairwellUtils';
import { useBuilding } from '../../Helpers/BuildingContext';

interface UseStairwellsReturn {
    stairwells: { [stairwellKey: string]: Stairwell };
    /** Natural order by name. */
    sortedStairwells: Stairwell[];
    submitting: boolean;
    error: string | null;
    createStairwell: (name: string) => Promise<Stairwell>;
    renameStairwell: (stairwellKey: string, name: string) => Promise<void>;
    deleteStairwell: (stairwellKey: string) => Promise<void>;
    clearError: () => void;
}

/**
 * Trappuppgångar of the current building, from the loaded BuildingContext
 * (`model.stairwells`); every mutation refetches the building, like useUnits.
 */
export const useStairwells = ({ buildingKey }: { buildingKey: string | null | undefined }): UseStairwellsReturn => {
    const { currentBuilding, fetchBuilding } = useBuilding();
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const stairwells = useMemo(() => {
        if (!currentBuilding || currentBuilding.key !== buildingKey) return {};
        return stairwellsApi.parseStairwells(currentBuilding.model.stairwells);
    }, [currentBuilding, buildingKey]);

    const sortedStairwells = useMemo(() => sortStairwells(stairwells), [stairwells]);

    const run = useCallback(async <T,>(fallbackMessage: string, action: (key: string) => Promise<T>): Promise<T> => {
        if (!buildingKey) {
            const message = 'Ingen byggnad vald';
            setError(message);
            throw new Error(message);
        }
        setSubmitting(true);
        setError(null);
        try {
            const result = await action(buildingKey);
            await fetchBuilding(buildingKey);
            return result;
        } catch (err) {
            console.error(fallbackMessage, err);
            setError(err instanceof Error ? err.message : fallbackMessage);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [buildingKey, fetchBuilding]);

    const createStairwell = useCallback((name: string) =>
        run('Kunde inte skapa trappuppgången', key => stairwellsApi.createStairwell(key, name)),
    [run]);

    const renameStairwell = useCallback((stairwellKey: string, name: string) =>
        run('Kunde inte byta namn på trappuppgången', key => stairwellsApi.renameStairwell(key, stairwellKey, name)),
    [run]);

    const deleteStairwell = useCallback((stairwellKey: string) =>
        run('Kunde inte ta bort trappuppgången', key => stairwellsApi.deleteStairwell(key, stairwellKey)),
    [run]);

    const clearError = useCallback(() => setError(null), []);

    return { stairwells, sortedStairwells, submitting, error, createStairwell, renameStairwell, deleteStairwell, clearError };
};
